import { type ChangeEvent, useLayoutEffect, useRef, useState } from "react";
import {
	MyPlannInput,
	type MyPlannInputProps,
} from "web_myplann/Shared/Components/MyPlannInput";
import { groupDigits } from "web_myplann/Shared/Utils/groupDigits";

const NBSP = " ";
const NON_DIGIT_PATTERN = /\D/g;
const COMMA_PATTERN = /,/g;
const DECIMAL_CHARS_PATTERN = /[^\d.,]/g;
const ALLOWED_DECIMAL_CHAR_PATTERN = /[\d.,]/;
const ALLOWED_INTEGER_CHAR_PATTERN = /\d/;
const DEFAULT_DECIMAL_PLACES = 2;

const formatIntegerForDisplay = (numeric: number): string =>
	groupDigits(String(Math.trunc(numeric)), NBSP);

const formatDecimalForDisplay = (numeric: number): string => {
	const [intPart, fracPart] = String(numeric).split(".");
	const intGrouped = groupDigits(intPart, NBSP);
	return fracPart ? `${intGrouped},${fracPart}` : intGrouped;
};

const formatIntegerDraft = (raw: string): string =>
	groupDigits(raw.replace(NON_DIGIT_PATTERN, ""), NBSP);

const formatDecimalDraft = (raw: string, decimalPlaces: number): string => {
	const cleaned = raw.replace(DECIMAL_CHARS_PATTERN, "").replace(".", ",");
	const sep = cleaned.indexOf(",");
	const intDigits = (sep === -1 ? cleaned : cleaned.slice(0, sep)).replace(
		NON_DIGIT_PATTERN,
		"",
	);
	const intGrouped = groupDigits(intDigits, NBSP);
	if (sep === -1) return intGrouped;
	const fracPart = cleaned
		.slice(sep + 1)
		.replace(COMMA_PATTERN, "")
		.slice(0, decimalPlaces);
	return `${intGrouped},${fracPart}`;
};

const parseInteger = (formatted: string): number | null => {
	const digits = formatted.replace(NON_DIGIT_PATTERN, "");
	if (!digits) return null;
	const numeric = Number(digits);
	return Number.isNaN(numeric) ? null : numeric;
};

const parseDecimal = (formatted: string): number | null => {
	const cleaned = formatted
		.replace(DECIMAL_CHARS_PATTERN, "")
		.replace(",", ".");
	if (!cleaned || cleaned === ".") return null;
	const numeric = Number(cleaned);
	return Number.isNaN(numeric) ? null : numeric;
};

const countAllowedCharsBefore = (
	raw: string,
	caret: number,
	allowedChar: RegExp,
): number => {
	let count = 0;
	for (let i = 0; i < caret && i < raw.length; i++) {
		if (allowedChar.test(raw[i])) count++;
	}
	return count;
};

const caretPositionAfterNAllowedChars = (
	formatted: string,
	n: number,
	allowedChar: RegExp,
): number => {
	if (n <= 0) return 0;
	let seen = 0;
	for (let i = 0; i < formatted.length; i++) {
		if (allowedChar.test(formatted[i])) {
			seen++;
			if (seen === n) return i + 1;
		}
	}
	return formatted.length;
};

type Props = Omit<MyPlannInputProps, "value" | "onChange" | "type"> & {
	value: unknown;
	onChange: (next: number | null) => void;
	/** `integer` accepts digits only; `decimal` also accepts a separator. */
	mode?: "integer" | "decimal";
	/** Only applies to `decimal` mode. */
	decimalPlaces?: number;
};

export const NumberMyPlannInput = ({
	value,
	onChange,
	onBlur,
	mode = "decimal",
	decimalPlaces = DEFAULT_DECIMAL_PLACES,
	...rest
}: Props) => {
	const [draft, setDraft] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const pendingCaretRef = useRef<number | null>(null);

	const isInteger = mode === "integer";
	const allowedChar = isInteger
		? ALLOWED_INTEGER_CHAR_PATTERN
		: ALLOWED_DECIMAL_CHAR_PATTERN;

	const formatForDisplay = (raw: unknown): string => {
		if (raw == null || raw === "") return "";
		const numeric = typeof raw === "number" ? raw : Number(raw);
		if (Number.isNaN(numeric)) return "";
		return isInteger
			? formatIntegerForDisplay(numeric)
			: formatDecimalForDisplay(numeric);
	};

	const displayValue = draft ?? formatForDisplay(value);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const next = e.target.value;
		const caret = inputRef.current?.selectionStart ?? next.length;
		const allowedBeforeCaret = countAllowedCharsBefore(
			next,
			caret,
			allowedChar,
		);
		const formatted = isInteger
			? formatIntegerDraft(next)
			: formatDecimalDraft(next, decimalPlaces);
		pendingCaretRef.current = caretPositionAfterNAllowedChars(
			formatted,
			allowedBeforeCaret,
			allowedChar,
		);
		setDraft(formatted);
		onChange(isInteger ? parseInteger(formatted) : parseDecimal(formatted));
	};

	useLayoutEffect(() => {
		const position = pendingCaretRef.current;
		if (position == null || inputRef.current == null) return;
		inputRef.current.setSelectionRange(position, position);
		pendingCaretRef.current = null;
	}, [displayValue]);

	const handleBlur = (
		e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setDraft(null);
		onBlur?.(e);
	};

	return (
		<MyPlannInput
			{...rest}
			multiline={false}
			inputProps={{
				...rest.inputProps,
				inputMode: isInteger ? "numeric" : "decimal",
			}}
			value={displayValue}
			onChange={handleChange}
			onBlur={handleBlur}
			inputRef={inputRef}
		/>
	);
};
